<template>
  <header class="relative isolate z-10 bg-secondary">
    <nav
      class="bg-secondary-600 mx-auto hidden lg:flex items-center justify-between px-10 py-1.5 sm:px-6 lg:px-5"
      aria-label="Global"
    >
      <div class="flex lg:hidden">
        <button
          type="button"
          class="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700"
          @click="mobileMenuOpen = true"
        >
          <span class="sr-only">{{ t('DjHeaderBottom.openMenu') }}</span>
          <Icon name="heroicons:bars-3-16-solid" class="w-7 h-7" aria-hidden="true" />
        </button>
      </div>

      <div class="hidden lg:flex lg:gap-x-12 items-center">
        <PopoverRoot :open="isOpenMenu" @update:open="isOpenMenu = $event">
          <PopoverTrigger as-child>
            <UButton
              variant="ghost"
              class="flex items-center gap-x-1 header-link-text ring-0 focus:ring-0 focus:outline-none"
            >
              <Icon
                :name="isOpenMenu ? 'heroicons:x-mark' : 'heroicons:bars-3'"
                class="w-7 h-7 header-link-text"
                aria-hidden="true"
              />
              <div class="header-link-text">
                {{ t('DjHeaderBottom.products').toUpperCase() }}
              </div>
            </UButton>
          </PopoverTrigger>

          <PopoverPortal>
            <PopoverContent :side-offset="5">
              <div
                v-if="NavCategories"
                class="bg-white overflow-y-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 lg:px-10 xl:gap-x-8 divide-x divide-[#99999954] border-b-[10px] border-b-secondary-500 w-screen h-[calc(100vh-190px)] shadow-xl"
              >
                <div class="overflow-auto">
                  <div
                    class="dj-category-title p-6 text-secondary-600 text-3xl leading-none font-bold uppercase"
                  >
                    {{ t('DjHeaderBottom.products') }}
                  </div>
                  <ul>
                    <li
                      v-for="(item, index) in NavCategories"
                      :key="index"
                      class="cursor-pointer dj-category-label mb-3 text-base font-bold uppercase main h-12 flex items-center px-4 py-6 box-border"
                      :class="`${item?.externalId} ${selectedMainMenu?.name === item?.name ? 'active' : ''}`"
                      @click="selectMainMenu(item)"
                    >
                      <NuxtImg
                        v-if="getNavCFIcon(item)"
                        :src="getNavCFIcon(item)"
                        class="size-5 mr-2"
                      />
                      <NuxtLink class="truncate">
                        {{ item?.name }}
                      </NuxtLink>
                    </li>
                  </ul>
                </div>
                <div
                  v-if="selectedMainMenu && selectedMainMenu.childrenCategories.length > 0"
                  class="w-full overflow-auto"
                >
                  <div class="dj-category-subtitle p-4">
                    <NuxtLink :to="getMainMenuPath(selectedMainMenu.externalId)" @click="closeMenu">
                      {{ selectedMainMenu?.name }}
                    </NuxtLink>
                  </div>
                  <ul>
                    <li
                      v-for="(subItem, index) in selectedMainMenu.childrenCategories"
                      :key="index"
                      class="cursor-pointer dj-category-label mb-3 text-base font-bold uppercase sub h-12 flex items-center px-4 py-6 box-border sub"
                      :class="`${selectedMainMenu.externalId} ${selectedSubMenu === subItem ? 'active' : ''}`"
                      @click="selectSubMenu(subItem)"
                    >
                      <NuxtLink>
                        {{ subItem?.name }}
                      </NuxtLink>
                    </li>
                  </ul>
                </div>
                <div
                  v-if="selectedSubMenu && selectedSubMenu.childrenCategories.length > 0"
                  class="w-full overflow-auto"
                >
                  <div class="dj-category-subtitle p-4">
                    <NuxtLink
                      :to="getSubMenuPath(selectedMainMenu.externalId, selectedSubMenu.externalId)"
                      @click="closeMenu"
                    >
                      {{ selectedSubMenu?.name }}
                    </NuxtLink>
                  </div>
                  <ul>
                    <li
                      v-for="(subSubItem, index) in selectedSubMenu.childrenCategories"
                      :key="index"
                      class="cursor-pointer dj-category-label mb-3 text-base font-bold uppercase sub h-12 flex items-center px-4 py-6 box-border truncate"
                      :class="`${selectedMainMenu.externalId} ${selectedSubSubMenu === subSubItem ? 'active' : ''}`"
                      @mouseover="selectSubSubMenu(subSubItem)"
                    >
                      <NuxtLink
                        :to="
                          getSubSubMenuPath(
                            selectedMainMenu.externalId,
                            selectedSubMenu.externalId,
                            subSubItem.externalId
                          )
                        "
                        class="truncate"
                        @click="closeMenu"
                      >
                        {{ subSubItem?.name }}
                      </NuxtLink>
                    </li>
                  </ul>
                </div>
              </div>
            </PopoverContent>
          </PopoverPortal>
        </PopoverRoot>
      </div>
      <div
        class="hidden gap-6 lg:flex lg:flex-1 lg:justify-end justify-center items-center text-sm"
      >
        <div class="hidden lg:flex lg:justify-end items-center gap-x-2">
          <Icon class="flex items-center header-bottom-icon" name="tabler:headset" />
          <span class="contact-text">{{ $t('DjHeaderBottom.advName') }} : {{ name }}</span>
        </div>
        <div class="hidden lg:flex lg:justify-end items-center gap-x-2">
          <Icon class="flex items-center header-bottom-icon" name="heroicons:phone" />
          <span class="contact-text">
            {{ phone }}
          </span>
        </div>
        <div class="hidden lg:flex lg:justify-end items-center gap-x-2">
          <Icon class="flex items-center header-bottom-icon" name="tabler:mail" />
          <span class="contact-text">
            {{ email }}
          </span>
        </div>
      </div>
    </nav>
    <DialogRoot v-model="mobileMenuOpen">
      <DialogPortal>
        <DialogOverlay class="fixed inset-0 z-10" />
        <DialogContent
          class="fixed inset-y-0 right-0 z-10 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-gray-900/10"
        >
          <div class="flex items-center justify-between">
            <a href="#" class="-m-1.5 p-1.5">
              <img
                class="h-8 w-auto"
                src="https://tailwindui.com/img/logos/mark.svg?color=indigo&shade=600"
                alt=""
              />
            </a>
            <button
              type="button"
              class="-m-2.5 rounded-md p-2.5 text-gray-700"
              @click="closeMobileMenu"
            >
              <span class="sr-only">{{ t('DjHeaderBottom.closeMenu') }}</span>
              <Icon name="heroicons:x-mark-16-solid" class="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div class="mt-6 flow-root">
            <div class="-my-6 divide-y divide-gray-500/10">
              <div class="space-y-2 py-6">
                <AccordionRoot v-model="accordionValue" type="single">
                  <AccordionItem value="products">
                    <AccordionHeader>
                      <AccordionTrigger
                        class="flex w-full items-center justify-between rounded-lg py-2 pl-3 pr-3.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                      >
                        {{ t('DjHeaderBottom.products') }}
                        <Icon
                          name="heroicons:chevron-down-20-solid"
                          class="h-5 w-5 flex-none transition-transform duration-200"
                          :class="{ 'rotate-180': accordionValue === 'products' }"
                          aria-hidden="true"
                        />
                      </AccordionTrigger>
                    </AccordionHeader>
                    <AccordionContent>
                      <div class="mt-2 space-y-2">
                        <a
                          href="#"
                          class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                        >
                          {{ t('DjHeaderBottom.products') }}
                        </a>
                        <a
                          href="#"
                          class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                        >
                          {{ t('DjHeaderBottom.marketplace') }}
                        </a>
                        <a
                          href="#"
                          class="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                        >
                          {{ t('DjHeaderBottom.company') }}
                        </a>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </AccordionRoot>
              </div>
              <div class="py-6">
                <a
                  href="#"
                  class="-mx-3 block rounded-lg px-3 py-2.5 text-base font-semibold leading-7 text-gray-900 hover:bg-gray-50"
                >
                  {{ t('DjHeaderBottom.connection') }}
                </a>
              </div>
            </div>
          </div>
        </DialogContent>
      </DialogPortal>
    </DialogRoot>
  </header>
</template>

<script setup lang="ts">
  import { useUtils } from '~/composables/useUtils';
  import { useI18n } from 'vue-i18n';
  import {
    PopoverRoot,
    PopoverTrigger,
    PopoverContent,
    PopoverPortal,
    DialogRoot,
    DialogPortal,
    DialogOverlay,
    DialogContent,
    AccordionRoot,
    AccordionItem,
    AccordionHeader,
    AccordionTrigger,
    AccordionContent,
  } from 'reka-ui';

  defineOptions({
    name: 'DjHeaderBottom',
  });

  interface SelectedMenuItem {
    childrenCategories: string | any[];
    externalId: any;
    name: string;
    customFieldValues: any[];
  }

  defineProps({
    email: {
      type: String,
      default: '',
    },
    phone: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
  });

  const selectedMainMenu = ref<SelectedMenuItem | null>(null);
  const selectedSubMenu = ref<SelectedMenuItem | null>(null);
  const selectedSubSubMenu = ref<SelectedMenuItem | null>(null);
  const router = useRouter();
  const localePath = useLocalePath();

  const { scrollToTop } = useUtils();

  const { t } = useI18n();
  const isOpenMenu = ref(false);

  const accordionValue = ref<string | undefined>(undefined);

  const selectMainMenu = (item: SelectedMenuItem | null): void => {
    if (item?.childrenCategories.length === 0) {
      closeMenu();
      router.push(localePath({ path: `/${item.externalId}` }));
    } else {
      selectedMainMenu.value = item;
    }
    selectedSubMenu.value = null;
  };

  const selectSubMenu = (item: SelectedMenuItem | null): void => {
    if (item?.childrenCategories.length === 0) {
      closeMenu();
      router.push(localePath({ path: `/${item.externalId}` }));
    } else {
      selectedSubMenu.value = item;
    }
    selectedSubSubMenu.value = null;
  };

  const selectSubSubMenu = (item: SelectedMenuItem | null): void => {
    if (item?.childrenCategories.length === 0) {
      closeMenu();
      router.push(localePath({ path: `/${item.externalId}` }));
    } else {
      selectedSubSubMenu.value = item;
    }
  };

  const getNavCFIcon = (item: SelectedMenuItem | null): string | undefined => {
    const icon = item?.customFieldValues?.find(
      (field: any) => field.customField?.externalId === 'Picto_Nav'
    );
    return icon?.value?.value;
  };

  const { getNavigationCategories } = useNavCategory();
  const { locale } = useI18n();

  const { categories: NavCategories } = await getNavigationCategories(locale.value);
  const mobileMenuOpen = ref(false);

  function openMenu(open: any): void {
    if (!open) scrollToTop({ top: 45, left: 0, behavior: 'smooth' });
  }

  function closeMenu() {
    isOpenMenu.value = false;
  }
  function closeMobileMenu() {
    mobileMenuOpen.value = false;
  }

  const getMainMenuPath = (externalId: string) => {
    return localePath(`/${externalId}`);
  };

  const getSubMenuPath = (mainExternalId: string, subExternalId: string) => {
    return localePath(`/${mainExternalId}/${subExternalId}`);
  };

  const getSubSubMenuPath = (
    mainExternalId: string,
    subExternalId: string,
    subSubExternalId: string
  ) => {
    return localePath(`/${mainExternalId}/${subExternalId}/${subSubExternalId}`);
  };
</script>

<style lang="scss" scoped>
  @use './DjHeaderBottom.scss';
</style>
